/**
 * Shared form helpers for the Trip wizard (CreateTripV2).
 * Kept separate from legacy CreateTrip.js so the original file stays untouched.
 */

export const deepTrimStrings = (value) => {
    if (typeof value === 'string') return value.trim();
    // Preserve binary upload payloads — File/Blob are objects but must not be walked.
    if (typeof File !== 'undefined' && value instanceof File) return value;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
    if (Array.isArray(value)) return value.map(deepTrimStrings);
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [key, deepTrimStrings(nestedValue)])
        );
    }
    return value;
};

export const isEffectivelyEmpty = (value) =>
    value === undefined
    || value === null
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);

export const TEXT_FORMAT_EXEMPT_KEYS = new Set([
    'primary_attraction',
    'location',
]);

export const isEmptyObject = (val) =>
    !val || (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0);

export const cleanRepeatable = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter((item) =>
        item
        && typeof item === 'object'
        && Object.keys(item).length > 0
    );
};

export const sanitizePayload = (obj) => {
    if (Array.isArray(obj)) return obj.map(sanitizePayload);
    if (obj && typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([k]) => k !== '_clientKey' && k !== 'pendingFile' && k !== 'hero_image')
                .map(([k, v]) => [k, sanitizePayload(v)])
        );
    }
    return (typeof obj === 'string' && obj === '') ? null : obj;
};

export const serializeHighlightsForApi = (highlights = []) => (
    (highlights || []).map(({ pendingFile, image, ...rest }) => {
        const attachmentId = rest.image_attachment_id || null;
        const payload = {
            id: rest.id,
            name: rest.name,
            location: rest.location,
            image_attachment_id: attachmentId,
        };
        // Images live in R2; only keep a legacy image URL when no attachment link exists.
        if (!attachmentId && image && !String(image).startsWith('blob:')) {
            payload.image = image;
        }
        return payload;
    })
);

export const prepareOverviewForApi = (overview = {}) => {
    const { hero_image, status: _status, key_highlights, ...restOverview } = overview;
    return {
        ...restOverview,
        key_highlights: serializeHighlightsForApi(key_highlights),
    };
};

export const toComparableDate = (value) => {
    if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
        return new Date(`1970-01-01T${value}:00`);
    }
    return new Date(value);
};

/** YYYY-MM-DD from a date input or ISO datetime, compared as a calendar day. */
export const toDateOnlyString = (value) => {
    if (value == null || value === '') return '';
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
};

/** Matches backend package-wizard past checks (`Asia/Kolkata`), not the browser TZ. */
export const PACKAGE_WIZARD_TIMEZONE = 'Asia/Kolkata';

export const todayDateOnlyString = (now = new Date()) => (
    // en-CA formats as YYYY-MM-DD
    new Intl.DateTimeFormat('en-CA', {
        timeZone: PACKAGE_WIZARD_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now)
);

export const isPastCalendarDate = (value) => {
    const day = toDateOnlyString(value);
    if (!day) return false;
    return day < todayDateOnlyString();
};

/**
 * Dates as last loaded from / saved to the server, so a saved row whose date has
 * since passed can still be re-saved. The backend applies the same rule.
 */
export const SAVED_DATES_KEY = '_saved_dates';

export const DEPARTURE_DATE_KEYS = [
    'departure_date',
    'return_date',
    'book_by_date',
    'early_bird_start_date',
    'early_bird_end_date',
    'last_minute_start_date',
    'last_minute_end_date',
];

export const ALLOCATION_DATE_KEYS = ['season_from', 'season_to'];

export const withSavedDates = (item, keys) => ({
    ...item,
    [SAVED_DATES_KEY]: Object.fromEntries(keys.map((k) => [k, toDateOnlyString(item?.[k])])),
});

export const isUnchangedSavedDate = (item, key, value) => {
    if (!item?.id) return false;
    const saved = item[SAVED_DATES_KEY]?.[key];
    return Boolean(saved) && saved === toDateOnlyString(value);
};

export const applyDefaults = (fieldsMap, data) => {
    const result = { ...(data || {}) };

    Object.entries(fieldsMap || {}).forEach(([key, field]) => {
        if (field.type === 'repeatable-form') {
            const raw = result[key];
            if (raw !== undefined && raw !== null && !Array.isArray(raw)) {
                console.warn(`applyDefaults: expected array for repeatable-form field "${key}", got`, raw);
            }
            const items = Array.isArray(raw) ? raw : [];
            result[key] = items.map((item) => applyDefaults(field.fields, { ...(item || {}) }));
            return;
        }

        const isEmpty = result[key] === undefined || result[key] === null || result[key] === '';
        if (field.default !== undefined && isEmpty) {
            result[key] = field.default;
        }

        const matchedSub = field.subFields?.find((sub) => {
            if (sub.showWhen === true) return result[key] === true;
            if (sub.showWhen === '__has_value__') {
                const v = result[key];
                return Array.isArray(v) ? v.length > 0 : !!v;
            }
            return sub.showWhen === result[key];
        });
        if (matchedSub) {
            Object.assign(result, applyDefaults(matchedSub.fields, result));
        }
    });

    return result;
};
